import { RotateCcwIcon, TimerIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { TimePicker } from '@mantine/dates';
import { timeEntryService, timeEntryServiceUtils } from '../services/timeEntryService';
import { colors } from '../theme/colors';
import type { EmployeeHours } from '../types';
import { calculateProgressState } from '../utils/progressUtils';
import { CircularProgressIndicator } from './CircularProgressIndicator';

interface EmployeeLoggedHoursProps {
  employeeHours: EmployeeHours;
  projectLoggedHours: number;
  onChange: (updatedHours: Partial<EmployeeHours>) => void;
  selectedDate?: Date;
  onSave?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  isAbsenceOrVacation?: boolean;
}

export const EmployeeLoggedHoursBox: React.FC<EmployeeLoggedHoursProps> = ({
  employeeHours,
  projectLoggedHours,
  onChange,
  selectedDate,
  onSave,
  onDelete,
  disabled = false,
  isAbsenceOrVacation = false,
}) => {
  const hoursBooked = useMemo(() => {
    return timeEntryServiceUtils.calculateHoursBooked(
      employeeHours.startTime,
      employeeHours.endTime,
      employeeHours.pauseMinutes
    );
  }, [employeeHours.startTime, employeeHours.endTime, employeeHours.pauseMinutes]);

  const progressIndicator = useMemo(() => {
    const progressState = calculateProgressState(hoursBooked, employeeHours.plannedHours, {
      near: 0.9,
    });

    if (!hoursBooked) {
      return (
        <span style={{ color: colors.accent.yellow, fontWeight: '600' }}>No hours logged yet</span>
      );
    }

    if (progressState.isOvertime) {
      return (
        <span style={{ color: colors.accent.blue, fontWeight: '600' }}>
          Flextime: +{(hoursBooked - employeeHours.plannedHours).toFixed(1)}h
        </span>
      );
    } else if (progressState.isNear) {
      return <span style={{ color: colors.accent.green, fontWeight: '600' }}>Near Plan Hours</span>;
    } else if (progressState.isUnder) {
      return (
        <span style={{ color: colors.accent.yellow, fontWeight: '600' }}>
          📊 {(employeeHours.plannedHours - hoursBooked).toFixed(1)} hours remaining
        </span>
      );
    } else {
      return (
        <span style={{ color: colors.accent.green, fontWeight: '600' }}>
          All Planned Hours Booked
        </span>
      );
    }
  }, [hoursBooked, employeeHours.plannedHours]);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pauseInputValue, setPauseInputValue] = useState<string>(
    employeeHours.pauseMinutes != null && employeeHours.pauseMinutes !== 0
      ? employeeHours.pauseMinutes.toString()
      : ''
  );
  const [pauseInputError, setPauseInputError] = useState<string | null>(null);

  useEffect(() => {
    if (employeeHours.pauseMinutes != null && employeeHours.pauseMinutes !== 0) {
      setPauseInputValue(employeeHours.pauseMinutes.toString());
      setPauseInputError(null);
    } else {
      setPauseInputValue('');
      setPauseInputError(null);
    }
  }, [employeeHours.pauseMinutes]);

  const handleInputChange = (field: keyof EmployeeHours, value: string | number) => {
    onChange({ [field]: value });
  };

  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      await onSave();
    } catch (error) {
      // Error saving logged hours
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!employeeHours.id || !employeeHours.isRecorded) return;

    const confirmed = window.confirm(
      'Are you sure you want to reset currenrt logged hours? This action cannot be undone.'
    );

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await timeEntryService.deleteLoggedHours(employeeHours.id);
      if (onDelete) {
        onDelete();
      } else {
        onChange({
          startTime: '',
          endTime: '',
          pauseMinutes: 0,
          isRecorded: false,
          id: undefined,
        });
      }
    } catch (error) {
      alert('Failed to delete logged hours. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isAbsenceOrVacation) {
    return (
      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: '8px',
          padding: '1rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          opacity: 0.6,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            color: colors.secondary.text,
            fontSize: '1rem',
            fontWeight: '500',
          }}
        >
          Not applicable
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: '8px',
          padding: '1rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px',
          gridTemplateRows: '1fr 1fr',
          maxHeight: '60px',
        }}
      >
        <h3
          style={{
            color: colors.primary.background,
            margin: '0 0 1rem 0',
            fontSize: '1rem',
            fontWeight: '600',
          }}
        >
          Employee Logged Hours
        </h3>
        <div
          style={{
            gridRowEnd: 'span 2',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            maxHeight: '50px',
          }}
        >
          {employeeHours.isRecorded && (
            <button
              onClick={handleReset}
              disabled={isDeleting || disabled}
              style={{
                height: '2.5rem',
                backgroundColor: isDeleting || disabled ? colors.neutral.grey[400] : colors.accent.red,
                color: colors.neutral.white,
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.7rem',
                fontWeight: '500',
                cursor: isDeleting || disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: isDeleting || disabled ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isDeleting && !disabled) {
                  e.currentTarget.style.backgroundColor = colors.accent.red + 'dd';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting && !disabled) {
                  e.currentTarget.style.backgroundColor = colors.accent.red;
                }
              }}
              title="Delete logged hours"
            >
              <RotateCcwIcon size={20} />
              {isDeleting ? 'Deleting...' : 'Reset'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || disabled}
            style={{
              height: '2.5rem',
              backgroundColor: isSaving || disabled ? colors.neutral.grey[400] : colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.7rem',
              fontWeight: '500',
              cursor: isSaving || disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              opacity: isSaving || disabled ? 0.7 : 1,
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              if (!isSaving && !disabled) {
                e.currentTarget.style.backgroundColor = colors.accent.blue + 'dd';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving && !disabled) {
                e.currentTarget.style.backgroundColor = colors.accent.blue;
              }
            }}
            title={employeeHours.isRecorded ? 'Update logged hours' : 'Add logged hours'}
          >
            <TimerIcon size={30} />
            {isSaving
              ? 'Saving...'
              : employeeHours.isRecorded
                ? 'Update'
                : 'Add Log Hours'}
          </button>
        </div>
        <h4
          style={{
            color: colors.secondary.text,
            margin: '0 0 auto 0',
            padding: '0 1rem 0 0',
            fontSize: '1rem',
            fontWeight: '500',
            opacity: 0.8,
          }}
        >
          {selectedDate?.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </h4>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {/* Time Inputs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.secondary.text,
              }}
            >
              Start
            </label>
            <TimePicker
              value={employeeHours?.startTime || ''}
              onChange={(value: string) => {
                handleInputChange('startTime', value);
              }}
              disabled={disabled}
              format="24h"
              minutesStep={5}
              withDropdown
              size="md"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.secondary.text,
              }}
            >
              End
            </label>
            <TimePicker
              value={employeeHours?.endTime || ''}
              onChange={(value: string) => {
                handleInputChange('endTime', value);
              }}
              disabled={disabled}
              format="24h"
              minutesStep={5}
              withDropdown
              size="md"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Pause Input */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
            }}
          >
            Pause (minutes)
          </label>
          <input
            type="number"
            min="0"
            max="500"
            step="1"
            value={pauseInputValue}
            onChange={(e) => {
              const inputValue = e.target.value;
              setPauseInputValue(inputValue);
              if (inputValue === '') {
                setPauseInputError(null);
                handleInputChange('pauseMinutes', 0);
              } else {
                const numValue = Number.parseFloat(inputValue);
                if (!Number.isNaN(numValue)) {
                  if (Number.isInteger(numValue)) {
                    setPauseInputError(null);
                    handleInputChange('pauseMinutes', numValue);
                  } else {
                    setPauseInputError('Pause must be a whole number (no decimals)');
                  }
                } else {
                  setPauseInputError('Pause must be a whole number (no decimals)');
                }
              }
            }}
            onBlur={(e) => {
              if (e.target.value === '') {
                setPauseInputError(null);
                handleInputChange('pauseMinutes', 0);
              } else {
                const numValue = Number.parseFloat(e.target.value);
                if (!Number.isNaN(numValue) && Number.isInteger(numValue)) {
                  setPauseInputError(null);
                  handleInputChange('pauseMinutes', numValue);
                }
              }
            }}
            disabled={disabled}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${pauseInputError ? colors.accent.red : colors.neutral.grey[300]}`,
              borderRadius: '6px',
              fontSize: '1rem',
              backgroundColor: disabled ? colors.neutral.grey[100] : colors.neutral.white,
              color: colors.secondary.text,
              cursor: disabled ? 'not-allowed' : 'text',
              opacity: disabled ? 0.6 : 1,
            }}
          />
          {pauseInputError && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '0.375rem',
                color: '#92400e',
                fontSize: '0.8125rem',
              }}
            >
              {pauseInputError}
            </div>
          )}
        </div>

        {/* Calculated Values */}
        <div
          style={{
            backgroundColor: colors.neutral.grey[50],
            borderRadius: '6px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '1rem',
              marginTop: '1rem',
              padding: '1rem',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: colors.secondary.text,
                  marginBottom: '0.25rem',
                }}
              >
                Planned Hours
              </div>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: colors.secondary.text,
                }}
              >
                {employeeHours.plannedHours.toFixed(2)}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: colors.secondary.text,
                  marginBottom: '0.25rem',
                }}
              >
                Actual Hours
              </div>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: colors.secondary.text,
                }}
              >
                {hoursBooked ? hoursBooked.toFixed(2) : '-.--'}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: colors.secondary.text,
                  marginBottom: '0.25rem',
                }}
              >
                Project Hours
              </div>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color:
                    projectLoggedHours === projectLoggedHours
                      ? colors.accent.blue
                      : colors.accent.red,
                }}
              >
                {projectLoggedHours.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Circular Progress Indicator */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
              }}
            >
              <CircularProgressIndicator
                value={hoursBooked}
                max={employeeHours.plannedHours}
                size={120}
                strokeWidth={8}
                colorScheme={{
                  under: colors.accent.yellow,
                  near: colors.accent.green,
                  overtime: colors.accent.blue,
                  default: colors.accent.green,
                }}
                thresholds={{
                  near: 0.9,
                  overtime: 1.0,
                }}
                allowOvertime={true}
                maxOvertime={1.5}
                format="percentage"
              />

              <div
                style={{
                  marginLeft: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: colors.secondary.text,
                    fontWeight: '500',
                  }}
                >
                  Progress Status
                </div>
                <div
                  style={{
                    fontSize: '1rem',
                    color: colors.secondary.text,
                    lineHeight: '1.4',
                  }}
                >
                  {progressIndicator}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};